import { createSimpleStore } from "@expert/state-utils";

/** How this page session started, captured once from the first observed agent status. */
export type AutoAvailableSessionStart = "unknown" | "offline" | "other";

/**
 * Session-scoped state for the auto-available pill. Intentionally NOT persisted: a real
 * re-login is a fresh page load, which resets this on its own.
 *
 * - `sessionStart` — set once from the first status we observe. Only a session that STARTED
 *   Offline is a genuine login; the countdown arms only then. A later Offline (logout, or a
 *   session that started in another aux status) must not arm, which is why we anchor on the
 *   initial status rather than on any Offline transition.
 * - `hasArmed` — the countdown has armed once this session. Never reset within a session, so a
 *   session that legitimately started Offline doesn't re-arm when logout drops it back to Offline.
 */
interface AutoAvailableState {
    hasArmed: boolean;
    sessionStart: AutoAvailableSessionStart;
    arm: () => void;
    markSessionStart: (start: AutoAvailableSessionStart) => void;
}

export const useAutoAvailableStore = createSimpleStore<AutoAvailableState>({
    devtools: {
        store: "auto-available",
        name: "workspace/expert-hub",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set) => ({
    hasArmed: false,
    sessionStart: "unknown",
    arm: () => set({ hasArmed: true }, false, "arm"),
    markSessionStart: (start) => set({ sessionStart: start }, false, "markSessionStart"),
}));
